import type { ReactNode } from "react";
import clsx from "clsx";
import styles from "./Checkbox.module.scss";

export interface CheckboxProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Visible text beside the box. Omit ONLY when something else already names the control — then pass `ariaLabel`. */
  label?: ReactNode;
  /** Accessible name when there is no visible `label`. One of the two is required or the box is unnamed. */
  ariaLabel?: string;
  disabled?: boolean;
  /** Native tooltip on the row, for the cases that carried a `title` before the migration. */
  title?: string;
  className?: string;
}

/**
 * A checkbox: a value you set and then submit with the rest of the form.
 *
 * **Not a `Switch`.** The distinction is the one already written on that component — a switch takes effect the
 * instant you flip it, a checkbox is staged and committed by a save. Picking the wrong one lies to the user
 * about whether their change has landed, so choose by *when the change applies*, not by how it looks.
 *
 * **Why this wraps a NATIVE input rather than drawing a box.** The reference does exactly this —
 * `.checkrow input { width:16px; height:16px; accent-color: var(--ac-color-accent) }` in the releases
 * specimen, and `.vcheck input` at 15px in advance-docs. `accent-color` recolours the browser's own control,
 * so we inherit the platform's keyboard handling, its indeterminate rendering, forced-colors/high-contrast
 * behaviour and its screen-reader semantics for free. A hand-drawn box has to re-earn all four, and the usual
 * version of it (a hidden input plus a styled span) is where checkboxes stop being announced correctly.
 *
 * The whole row is the hit target because the `<input>` lives inside the `<label>` — clicking the text toggles
 * it, which is the behaviour the raw markup this replaces already had.
 */
export function Checkbox({ checked, onChange, label, ariaLabel, disabled, title, className }: CheckboxProps) {
  return (
    <label className={clsx(styles.row, className)} title={title} data-disabled={disabled || undefined}>
      <input
        type="checkbox"
        className={styles.box}
        checked={checked}
        disabled={disabled}
        aria-label={label === undefined ? ariaLabel : undefined}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label !== undefined && <span className={styles.label}>{label}</span>}
    </label>
  );
}
